import React from 'react';
import Image from 'next/image';
import { formatCurrency, formatNumberWithCommas } from '@src/v3/utils/helper';
import Link from 'next/link';
import PropertyCardPills from './propertyCardPills';
import { PropertyCardCompactProps } from '@src/v3/interfaces/components/propertyCard';
import { cn } from '@src/v3/lib/utils';

const PropertyCardCompact: React.FC<PropertyCardCompactProps> = ({
  image,
  address,
  locationDetails,
  price,
  netEffectiveRent,
  bedroom,
  bathroom,
  squareFootage,
  href = '#',
  variant = 'default',
  widthClassName = 'w-100vw sm:w-full sm:min-w-[365px] max-w-[365px]',
}) => {
  if (variant === 'singleUnit') {
    return (
      <Link
        href={href}
        scroll={false}
        target="_blank"
        className={cn('pb-6 block last:pb-0 font-geist', widthClassName)}
      >
        <div className="relative rounded-lg cursor-pointer">
          <div className="rounded-lg flex flex-col">
            {/* Image with Pills overlay */}
            <div className="relative bg-white overflow-hidden rounded-t-xl">
              <div className="w-full min-h-[195px] sm:h-[195px]">
                <Image
                  src={image || '/no-image.png'}
                  fill
                  alt="property image"
                  className="object-cover"
                />
              </div>
              {/* Property Details Pill */}
              <div className="absolute top-3 left-3">
                <PropertyCardPills
                  bath={bathroom}
                  bedroom={bedroom}
                  squareFootage={squareFootage}
                />
              </div>
            </div>

            <div className="bg-white px-3 py-3 sm:pb-4 sm:pt-3 rounded-b-xl">
              {/* Price Section */}
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-lg sm:text-xl font-semibold text-black-dark">
                  {formatCurrency(price || 0)}/mo
                </span>
                {netEffectiveRent && netEffectiveRent !== price ? (
                  <span className="text-xs sm:text-sm text-black-dark/50 font-medium">
                    {formatCurrency(netEffectiveRent)}/m Net Rent
                  </span>
                ) : null}
              </div>
              {/* Address */}
              <p className="mt-1 text-sm text-gray-1000 font-normal line-clamp-1">
                {address}
              </p>
              {locationDetails && (
                <p className="text-sm font-normal text-gray-1000/50 truncate mt-1">
                  {locationDetails}
                </p>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Default compact horizontal layout (for multiple properties)
  return (
    <Link
      href={href}
      scroll={false}
      target="_blank"
      className="pb-4 block last:pb-0 w-full max-w-[350px] min-w-[350px] font-geist"
    >
      <div className="flex gap-3 cursor-pointer">
        {/* Property Image */}
        <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
          <Image
            src={image || '/no-image.png'}
            fill
            alt="property image"
            className="object-cover"
          />
        </div>

        {/* Property Details */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Price */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl font-semibold text-gray-1000">
              {formatCurrency(price || 0)}/m
            </span>
            {/* <span className="text-sm font-medium text-gray-1000/50">
              Net Rent
            </span> */}
          </div>

          {/* Address */}
          <div className="flex flex-col text-sm text-gray-1000 font-normal">
            <p className="font-medium truncate">{address}</p>
            {locationDetails && (
              <p className="font-normal text-gray-1000/50 truncate">
                {locationDetails}
              </p>
            )}
          </div>

          {/* Property Attributes */}
          <div className="flex items-center gap-2 text-base text-gray-1000/50 font-normal">
            <span>{bedroom > 0 ? `${bedroom} bd` : 'studio'}</span>
            <span className="text-gray-300">|</span>
            {bathroom > 0 && (
              <>
                <span>{bathroom} ba</span>
                {squareFootage && squareFootage > 0 ? (
                  <span className="text-gray-300">|</span>
                ) : null}
              </>
            )}
            {squareFootage && squareFootage > 0 ? (
              <span>{formatNumberWithCommas(squareFootage)} sqft</span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCardCompact;
